// 初始化 Member Savings 脚本作用域。
(() => {
  // 查找单个元素。
  const $ = (selector, context = document) => context?.querySelector(selector) || null;
  // 查找多个元素并返回数组。
  const $$ = (selector, context = document) => [...(context?.querySelectorAll(selector) || [])];

  const PRODUCT_HOST = '.gw-rd-free-product-slider-widget-placeholder';
  const SPEND_HOST = '.gw-rd-ways-to-spend-page-widget-placeholder';
  const AVAILABLE_HOST = '.gw-rd-available-rewards-page-widget-placeholder';
  const HISTORY_HOST = '.gw-rd-history-page-widget-placeholder';
  const instances = new WeakMap();

  // 解析 Growave SDK 返回的数据结构。
  function unwrap(result) {
    if (Array.isArray(result)) return result;
    if (result?.error || result?.success === false || result?.isError) return [];
    return result?.data ?? result?.result?.data ?? result?.value ?? result?.result ?? [];
  }

  // 从积分兑换规则中整理出商品数据。
  function buildProducts(rules) {
    if (!Array.isArray(rules)) return [];

    const products = [];
    // 遍历每条兑换规则。
    rules.forEach(entry => {
      const rule = entry?.rule || entry;
      const metadata = entry?.metadata || {};
      const rewards = Array.isArray(entry?.rewards) ? entry.rewards : [entry?.rewards || {}];
      const ruleType = String(rule?.ruleType || rule?.type || '').toUpperCase();
      const sourceProducts = metadata.products || metadata.collectionProducts || [];

      // 遍历规则里的奖励。
      rewards.forEach(reward => {
        const productId = String(reward?.productId || reward?.product?.id || '');
        if (ruleType !== 'FREE_PRODUCT' || !sourceProducts.length) return;

        const candidates = productId
          ? sourceProducts.filter(product => String(product?.id || product?.productId) === productId)
          : sourceProducts;

        // 遍历匹配到的商品。
        candidates.forEach(product => {
          const id = String(product?.id || product?.productId || '');
          if (!id) return;

          const variants = (metadata.variants || []).filter(variant =>
            String(variant?.productId || variant?.product_id || id) === id);
          const image = product?.image?.url || product?.image?.src || product?.featuredImage?.url ||
            product?.featured_image || product?.imageUrl || product?.image;
          const handle = product?.handle || product?.url?.split('/products/')[1]?.split(/[?#]/)[0] || '';

          products.push({
            key: `${rule?.ruleId || rule?.id || entry?.id || id}-${id}`,
            productId: id,
            ruleId: rule?.ruleId || rule?.id || entry?.id,
            title: product?.title || product?.name || '',
            subtitle: [variants[0]?.sku, variants[0]?.title !== 'Default Title' ? variants[0]?.title : ''].filter(Boolean).join(', '),
            handle,
            image: typeof image === 'string' ? image : '',
            points: rule?.pointsPrice || rule?.points || rule?.pointsCost || rule?.requiredPoints || rule?.pointsToRedeem ||
              rule?.redeemPoints || reward?.points || reward?.pointsCost || '',
            discount: reward?.percents != null ? `${reward.percents}% OFF` :
              reward?.amount != null ? `${reward.amount} OFF` : '',
            variantId: variants[0]?.id || reward?.variantId || '',
            variants
          });
        });
      });
    });

    return products.filter((item, index, list) => list.findIndex(other => other.key === item.key) === index);
  }

  // 创建并显示自定义商品卡。
  function renderProducts(element, state, products) {
    if (!state.productsRoot) return;

    const fragment = document.createDocumentFragment();
    // 为每个商品创建一张卡片。
    products.forEach(product => {
      const card = document.createElement('article');
      card.className = 'member-savings__product-card';
      card.dataset.productId = product.productId;
      card.dataset.ruleId = product.ruleId || '';

      // 创建商品图片。
      if (product.image) {
        const image = document.createElement('img');
        image.className = 'member-savings__product-image';
        image.src = product.image;
        image.alt = product.title;
        image.loading = 'lazy';
        card.append(image);
      }

      // 创建商品标题、优惠信息和兑换按钮。
      const body = document.createElement('div');
      body.className = 'member-savings__product-info';
      const row = document.createElement('div');
      row.className = 'member-savings__product-row';
      const details = document.createElement('div');
      details.className = 'member-savings__product-details';

      const title = document.createElement('a');
      title.className = 'member-savings__product-title';
      title.textContent = product.title;
      if (product.handle) title.href = `/products/${encodeURIComponent(product.handle)}`;
      details.append(title);

      const meta = document.createElement('p');
      meta.className = 'member-savings__product-reward';
      meta.textContent = [product.discount, product.points !== '' ? `${product.points} Points` : ''].filter(Boolean).join(' + ');
      body.prepend(meta);

      if (product.subtitle) {
        const subtitle = document.createElement('p');
        subtitle.className = 'member-savings__product-subtitle';
        subtitle.textContent = product.subtitle;
        details.append(subtitle);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'member-savings__action';
      button.textContent = element.dataset.redeemLabel || 'Redeem Now';
      button.disabled = state.pointBalance != null && Number(product.points) > state.pointBalance;
      button.setAttribute('aria-label', `${button.textContent}: ${product.title}`);
      // 点击自定义按钮时打开 Growave 的兑换弹窗。
      button.addEventListener('click', () => openNativeReward(element, product));
      row.append(details, button);
      body.append(row);

      card.append(body);
      fragment.append(card);
    });

    state.productsRoot.replaceChildren(fragment);
  }

  // 点击对应的 Growave 商品卡以打开原生兑换弹窗。
  function openNativeReward(element, product) {
    const root = $(PRODUCT_HOST, element)?.shadowRoot;
    const nativeCards = $$('.gw-rd-free-product-slider-widget-card', root);
    const target = nativeCards.find(card =>
      $('h2', card)?.textContent.trim().toLowerCase() === product.title.trim().toLowerCase());
    const button = $('.gw-rd-free-product-slider-widget-card__link', target);
    if (button) button.click();
  }

  // 隐藏 Ways to Spend 中重复展示的商品兑换项。
  function hideProductCoupons(element) {
    if (element.dataset.separateProductRewards !== 'true') return;

    // 遍历 Growave 的积分兑换列表。
    $$(SPEND_HOST).forEach(host => {
      // 隐藏其中的 Free Product 项。
      $$('.gw-rd-list__item', host.shadowRoot).forEach(item => {
        if ($('.gw-rd-page-widget-card--free-product-rule', item)) {
          item.setAttribute('data-member-savings-product-coupon', '');
          item.hidden = true;
        }
      });
      host.setAttribute('data-member-savings-separated', '');
    });
  }

  // 将自定义样式加载到 Growave 的 Shadow Root。
  function ensureWidgetStyles(element, root) {
    if (!element.dataset.widgetStylesUrl || $('[data-rewards-page-styles]', root)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = new URL(element.dataset.widgetStylesUrl, window.location.href).href;
    link.dataset.rewardsPageStyles = '';
    root.append(link);
  }

  // 给 Available Rewards 卡片添加自定义使用按钮。
  function addUseButtons(element, root) {
    // 遍历可用奖励卡片。
    $$('.gw-rd-page-widget-card', root).forEach(card => {
      const native = $('.gw-rd-page-widget-card-btn--absolute', card);
      if (!native) return;

      const existing = $('[data-member-savings-use]', card);
      if (existing) {
        existing.disabled = native.disabled || native.getAttribute('aria-disabled') === 'true';
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'member-savings__action';
      button.dataset.memberSavingsUse = '';
      button.textContent = element.dataset.useLabel || 'Use Now';
      button.disabled = native.disabled || native.getAttribute('aria-disabled') === 'true';
      // 点击自定义按钮时触发 Growave 原生按钮。
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        const action = $('.gw-rd-page-widget-card-btn--absolute', card);
        if (action && !action.disabled && action.getAttribute('aria-disabled') !== 'true') action.click();
      });
      card.append(button);
    });
  }

  // 隐藏 History 中包含 Free product 链接的父元素。
  function hideHistoryProductTypes(root = document) {
    $$('.gw-rd-history-page-widget__table-content a', root).forEach(link => {
      const text = link.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      if (text.includes('free product') && link.parentElement) {
        link.parentElement.style.display = 'none';
      }
    });
  }

  // 监听 Growave 区块变化并更新自定义展示。
  function watchWidgets(element, state) {
    // 查找已加载的 Growave 区块并安装监听器。
    const refresh = () => {
      hideHistoryProductTypes();
      // 监听 History 更新，兼容普通 DOM 和 Shadow DOM。
      $$(HISTORY_HOST).forEach(host => {
        const root = host.shadowRoot || host;
        if (!state.widgetRoots.has(root)) {
          const observer = new MutationObserver(() => hideHistoryProductTypes(root));
          observer.observe(root, { childList: true, subtree: true, characterData: true });
          state.widgetRoots.set(root, observer);
        }
        hideHistoryProductTypes(root);
      });

      // 监听并处理积分兑换列表。
      $$(SPEND_HOST).forEach(host => {
        if (host.shadowRoot && !state.widgetRoots.has(host.shadowRoot)) {
          ensureWidgetStyles(element, host.shadowRoot);
          // 列表内容变化后隐藏重复的商品兑换项。
          const observer = new MutationObserver(() => hideProductCoupons(element));
          observer.observe(host.shadowRoot, { childList: true, subtree: true });
          state.widgetRoots.set(host.shadowRoot, observer);
        }
        if (state.loaded) hideProductCoupons(element);
      });

      // 监听并处理可用奖励列表。
      $$(AVAILABLE_HOST).forEach(host => {
        const root = host.shadowRoot;
        if (!root) return;

        if (!state.widgetRoots.has(root)) {
          ensureWidgetStyles(element, root);
          // 卡片变化后补充自定义使用按钮。
          const observer = new MutationObserver(() => addUseButtons(element, root));
          observer.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled']
          });
          state.widgetRoots.set(root, observer);
        }
        addUseButtons(element, root);
      });
    };

    // 页面区块变化时重新查找 Growave 区块。
    state.widgetObserver = new MutationObserver(refresh);
    state.widgetObserver.observe(document.documentElement, { childList: true, subtree: true });
    refresh();
  }

  // 从 SDK 获取积分兑换数据并更新页面。
  async function loadRewards(element, state) {
    const sdk = window.gw?.sdk;
    if (state.loading || state.loaded) return;
    if (!sdk) {
      element.dataset.memberSavingsSdkState = 'sdk-unavailable';
      return;
    }

    const moduleName = sdk.common?.constants?.modules?.REWARDS;
    if (moduleName && sdk.runIfModuleReady && !state.rewardsModuleReady) {
      state.loading = true;
      element.dataset.memberSavingsSdkState = 'waiting-rewards-module';
      // 等待 Growave Rewards 模块就绪后重新加载。
      sdk.runIfModuleReady(moduleName, () => {
        state.rewardsModuleReady = true;
        state.loading = false;
        loadRewards(element, state);
      });
      return;
    }

    const rewards = sdk.rewards?.methods || sdk.rewards;
    if (!rewards?.getRewardsSpendingRules) {
      element.dataset.memberSavingsSdkState = 'rewards-module-unavailable';
      return;
    }

    element.dataset.memberSavingsSdkState = 'loading';
    state.loading = true;
    try {
      const locale = document.documentElement.lang || 'en';
      const rules = unwrap(await rewards.getRewardsSpendingRules(locale));
      const products = buildProducts(rules);
      if (!products.length) throw new Error('Growave SDK returned no free-product rewards');

      if (typeof rewards.getRewardsProgramAccount === 'function') {
        // 读取会员当前积分余额。
        try {
          const account = unwrap(await rewards.getRewardsProgramAccount(locale));
          state.pointBalance = Number.isFinite(Number(account?.points?.balance))
            ? Number(account.points.balance)
            : null;
        } catch (error) {
          state.pointBalance = null;
        }
      }

      renderProducts(element, state, products);
      state.loaded = true;
      element.dataset.memberSavingsSdkState = 'loaded';

      const source = $(PRODUCT_HOST, element);
      source?.classList.add('member-savings__sdk-source--ready');
      source?.setAttribute('aria-hidden', 'true');
      if (source) source.hidden = true;
      $$('a, button, [tabindex]', source?.shadowRoot).forEach(item => item.setAttribute('tabindex', '-1'));
      hideProductCoupons(element);
    } catch (error) {
      element.dataset.memberSavingsSdkState = 'load-error';
      console.error('[Member Savings] Could not load Growave rewards from SDK.', error);
    } finally {
      state.loading = false;
    }
  }

  // 清理区块对应的定时器和监听器。
  function cleanup(element) {
    const state = instances.get(element);
    if (!state) return;

    clearInterval(state.timer);
    state.widgetObserver?.disconnect();
    state.widgetRoots.forEach(observer => observer.disconnect());
    document.removeEventListener('gwSdkReady', state.onSdkReady);
    instances.delete(element);
  }

  // 初始化一个 Member Savings 区块。
  function init(element) {
    if (instances.has(element)) return;

    const state = {
      productsRoot: $('[data-member-savings-products]', element),
      pointBalance: null,
      loading: false,
      loaded: false,
      rewardsModuleReady: false,
      retries: 0,
      widgetRoots: new Map(),
      timer: null,
      widgetObserver: null,
      onSdkReady: null
    };

    // Growave SDK 就绪时加载奖励数据。
    state.onSdkReady = () => loadRewards(element, state);
    instances.set(element, state);
    element.dataset.memberSavingsSdkState = 'waiting';
    document.addEventListener('gwSdkReady', state.onSdkReady);
    state.onSdkReady();
    watchWidgets(element, state);

    // 定时检查 Growave SDK 是否已经加载。
    state.timer = setInterval(() => {
      state.onSdkReady();
      if (++state.retries >= 30) {
        clearInterval(state.timer);
        if (!state.loaded) {
          element.dataset.memberSavingsSdkState = window.gw?.sdk ? 'rewards-module-unavailable' : 'sdk-unavailable';
        }
      }
    }, 1000);
  }

  // 初始化指定范围内的 Member Savings 区块。
  function initWithin(scope) {
    if (scope.matches?.('member-savings')) init(scope);
    $$('member-savings', scope).forEach(init);
  }

  // 清理指定范围内的 Member Savings 区块。
  function cleanupWithin(scope) {
    if (scope.matches?.('member-savings')) cleanup(scope);
    $$('member-savings', scope).forEach(cleanup);
  }

  initWithin(document);

  // 区块加载后执行初始化。
  document.addEventListener('shopify:section:load', event => initWithin(event.target));
  // 区块卸载后执行清理。
  document.addEventListener('shopify:section:unload', event => cleanupWithin(event.target));

  // 处理页面中动态增加或移除的区块。
  const sectionObserver = new MutationObserver(mutations => {
    // 检查本次 DOM 变化涉及的节点。
    mutations.forEach(mutation => {
      // 初始化新加入页面的区块。
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) initWithin(node);
      });
      // 清理已从页面移除的区块。
      mutation.removedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE && !node.isConnected) cleanupWithin(node);
      });
    });
  });
  sectionObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
